// src/components/PaletProductionReport/PaletProductionReport.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './PaletProductionReport.css';

function PaletProductionReport() {
  const [paletData, setPaletData] = useState([]);
  const [faultData, setFaultData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortCriteria, setSortCriteria] = useState('total_palets');

  useEffect(() => {
    const fetchPaletData = async () => {
      try {
        const response = await axios.get('http://192.168.0.126:5000/production/employee-palets');
        setPaletData(response.data);
      } catch (err) {
        console.error('Error al obtener los datos de palets:', err);
        setError('Error al obtener los datos de palets.');
      }
    };

    const fetchFaultData = async () => {
      try {
        const response = await axios.get('http://192.168.0.126:5000/production/employee-faults');
        setFaultData(response.data);
      } catch (err) {
        console.error('Error al obtener los datos de fallos:', err);
        setError('Error al obtener los datos de fallos.');
      }
    };

    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchPaletData(), fetchFaultData()]);
      setLoading(false);
    };

    fetchData();
  }, []);

  const combinedData = useMemo(() => {
    const employeeNames = [
      ...new Set(
        [...paletData, ...faultData]
          .map((item) => item.employee_name)
          .filter(Boolean)
      ),
    ];

    return employeeNames.map((employee_name) => {
      const palet =
        paletData.find((p) => p.employee_name === employee_name) || {
          total_palets: 0,
          semielaborado: 0,
        };
      const fault =
        faultData.find((f) => f.employee_name === employee_name) || {
          total_amount: 0,
        };

      const semiVal =
        palet.semielaborado === 1 ||
        palet.semielaborado === '1' ||
        palet.semielaborado === true
          ? 1
          : 0;

      return {
        employee_name,
        total_palets: Number(palet.total_palets) || 0,
        total_amount: Number(fault.total_amount) || 0,
        semielaborado: semiVal,
      };
    });
  }, [paletData, faultData]);

  const sortedData = useMemo(() => {
    return [...combinedData].sort((a, b) => {
      let comparison = 0;

      if (sortCriteria === 'employee_name') {
        comparison = a.employee_name.localeCompare(b.employee_name, 'es', {
          sensitivity: 'base',
        });
      } else if (sortCriteria === 'total_palets') {
        comparison = a.total_palets - b.total_palets;
      } else if (sortCriteria === 'total_amount') {
        comparison = a.total_amount - b.total_amount;
      } else if (sortCriteria === 'semielaborado') {
        comparison = a.semielaborado - b.semielaborado;
      }

      if (comparison === 0) {
        return a.employee_name.localeCompare(b.employee_name, 'es', {
          sensitivity: 'base',
        });
      }

      return sortOrder === 'desc' ? comparison * -1 : comparison;
    });
  }, [combinedData, sortCriteria, sortOrder]);

  const totals = useMemo(
    () =>
      combinedData.reduce(
        (acc, row) => ({
          employees: acc.employees + 1,
          palets: acc.palets + row.total_palets,
          faults: acc.faults + row.total_amount,
          semi: acc.semi + row.semielaborado,
        }),
        { employees: 0, palets: 0, faults: 0, semi: 0 }
      ),
    [combinedData]
  );

  const sortData = (criteria) => {
    let nextOrder = 'desc';

    if (criteria === sortCriteria) {
      nextOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    } else if (criteria === 'employee_name') {
      nextOrder = 'asc';
    }

    setSortCriteria(criteria);
    setSortOrder(nextOrder);
  };

  const sortIcon = (criteria) => {
    if (criteria !== sortCriteria) return '↕';
    return sortOrder === 'desc' ? '↓' : '↑';
  };

  if (loading) return <p className="loading">Cargando datos...</p>;
  if (error) return <p className="error">{error}</p>;

  return (
    <div className="palet-report">
      <div className="palet-report__header">
        <div>
          <p className="palet-report__eyebrow">Resumen por empleado</p>
          <h4 className="palet-report__title">PRODUCCIÓN</h4>
        </div>

        <div className="palet-report__summary" aria-label="Resumen de producción">
          <div className="palet-report__stat">
            <span>Empleados</span>
            <strong>{totals.employees}</strong>
          </div>
          <div className="palet-report__stat">
            <span>Palets</span>
            <strong>{totals.palets}</strong>
          </div>
          <div className="palet-report__stat">
            <span>Saldos</span>
            <strong>{totals.faults}</strong>
          </div>
          <div className="palet-report__stat">
            <span>Semielab.</span>
            <strong>{totals.semi}</strong>
          </div>
        </div>
      </div>

      {sortedData.length === 0 ? (
        <p className="palet-report__empty">No hay datos de producción para mostrar.</p>
      ) : (
        <div className="palet-report__table-wrap">
          <table className="palet-report__table">
            <thead>
              <tr>
                <th className="palet-report__rank">#</th>
                <th>
                  <button type="button" onClick={() => sortData('employee_name')}>
                    EMPLEADO <span>{sortIcon('employee_name')}</span>
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => sortData('total_palets')}>
                    PROD TOTAL <span>{sortIcon('total_palets')}</span>
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => sortData('total_amount')}>
                    SALDOS TOTAL <span>{sortIcon('total_amount')}</span>
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => sortData('semielaborado')}>
                    SEMIELABORADO <span>{sortIcon('semielaborado')}</span>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, index) => (
                <tr
                  key={row.employee_name}
                  className={
                    sortCriteria === 'total_palets' &&
                    sortOrder === 'desc' &&
                    index < 3
                      ? 'palet-report__top-row'
                      : ''
                  }
                >
                  <td className="palet-report__rank">{index + 1}</td>
                  <td className="palet-report__employee">{row.employee_name}</td>
                  <td
                    className={
                      sortCriteria === 'total_palets'
                        ? 'palet-report__active-cell'
                        : ''
                    }
                  >
                    {row.total_palets}
                  </td>
                  <td
                    className={
                      sortCriteria === 'total_amount'
                        ? 'palet-report__active-cell'
                        : ''
                    }
                  >
                    {row.total_amount}
                  </td>
                  <td
                    className={
                      sortCriteria === 'semielaborado'
                        ? 'palet-report__active-cell'
                        : ''
                    }
                  >
                    {row.semielaborado === 1 ? 'Sí' : 'No'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PaletProductionReport;
